// Global Application State
// Import domain models from features
import features.auth.domain.User
import features.teams.domain.Team
import features.members.domain.Member
import features.shifts.domain.ShiftTemplate
import features.schedule.domain.Schedule
import features.attendance.domain.AttendanceRecord
import features.overtime.domain.OvertimeRequest
import features.timeoff.domain.*
import features.notifications.domain.Notification
// Data classes now imported from features
// See imports at top of file

object AppState {
    var currentUser: User? = null
    var teams: MutableList<Team> = mutableListOf()
    var currentTeam: Team? = null
    var currentPage: String = "login"
    var currentTab: String = "members" // For team detail page
    var isLoading: Boolean = false
    var authInitialized: Boolean = false
    var teamsLoaded: Boolean = false // Flag to prevent infinite loop when loading teams
    
    // Team-specific data
    val members: MutableMap<String, MutableList<Member>> = mutableMapOf()
    val shiftTemplates: MutableMap<String, MutableList<ShiftTemplate>> = mutableMapOf()
    val attendance: MutableMap<String, MutableList<AttendanceRecord>> = mutableMapOf()
    val overtime: MutableMap<String, MutableList<OvertimeRequest>> = mutableMapOf()
    val timeOffRequests: MutableMap<String, MutableList<TimeOffRequest>> = mutableMapOf()
    val leaveTypes: MutableMap<String, MutableList<LeaveType>> = mutableMapOf()
    val leaveCategories: MutableMap<String, MutableList<LeaveCategory>> = mutableMapOf()
    val schedules: MutableMap<String, Schedule> = mutableMapOf()
    val holidays: MutableMap<String, MutableList<features.attendance.domain.Holiday>> = mutableMapOf()
    
    // Notification state
    var notifications: MutableList<Notification> = mutableListOf()
    var unreadNotificationCount: Int = 0
    
    // Member management state
    var memberSearchQuery: String = ""
    var memberFilterRole: String = "All"
    var memberSortBy: String = "name"
    var selectedMemberIds: MutableSet<String> = mutableSetOf()
    
    // Attendance filter state
    var attendanceFilterStartDate: String? = null
    var attendanceFilterEndDate: String? = null
    
    // Schedule selection state
    var selectedScheduleCells: MutableSet<String> = mutableSetOf() // Format: "memberId:day"
    var lastSelectedCell: String? = null // For shift+click range selection
    
    // Schedule filter state
    var scheduleMonth: Int = js("new Date().getMonth() + 1") as Int
    var scheduleYear: Int = js("new Date().getFullYear()") as Int
    
    fun initMockData() {
        currentUser = User(
            id = "user1",
            name = "John Doe",
            email = "john@example.com",
            avatar = "https://ui-avatars.com/api/?name=John+Doe&background=06b6d4&color=fff"
        )
        
        teams = mutableListOf(
            Team(
                id = "team1",
                name = "Solo Office",
                ownerId = "user1",
                role = "owner",
                memberCount = 7,
                shiftCount = 3,
                slug = "solo-office"
            ),
            Team(
                id = "team2",
                name = "JTA Branch",
                ownerId = "user2",
                role = "member",
                memberCount = 5,
                shiftCount = 4,
                slug = "jta-branch"
            )
        )
        
        // Init members for team1
        members["team1"] = mutableListOf(
            Member("user1", "John Doe", "john@example.com", "owner"),
            Member("user2", "Purwanti", "purwanti@example.com", "admin"),
            Member("user3", "James Barkich", "james@example.com", "member")
        )
        
        // Init shift templates for team1
        shiftTemplates["team1"] = mutableListOf(
            ShiftTemplate("S1", "S1", "#10b981", "07:30", "16:30"),
            ShiftTemplate("S2", "S2", "#f59e0b", "14:00", "22:00"),
            ShiftTemplate("S3", "S3", "#8b5cf6", "22:00", "07:00"),
            ShiftTemplate("OFF", "OFF", "#ef4444", "-", "-")
        )
        
        // Init attendance for team1 - More comprehensive data
        attendance["team1"] = mutableListOf(
            // Today's records (2026-01-20)
            AttendanceRecord(1, "user1", "2026-01-20", "07:25", "-", "Present", "Office HQ, Jakarta"),
            AttendanceRecord(2, "user2", "2026-01-20", "-", "-", "Absent", "-"),
            AttendanceRecord(3, "user3", "2026-01-20", "21:55", "-", "Present", "Office HQ, Jakarta"),
            // Previous days
            AttendanceRecord(4, "user1", "2026-01-16", "07:25", "16:30", "Present", "Office HQ, Jakarta"),
            AttendanceRecord(5, "user2", "2026-01-16", "07:45", "16:35", "Late", "Remote - Home"),
            AttendanceRecord(6, "user3", "2026-01-16", "21:55", "07:05", "Present", "Office HQ, Jakarta"),
            AttendanceRecord(7, "user1", "2026-01-15", "07:30", "16:35", "Present", "Office HQ, Jakarta"),
            AttendanceRecord(8, "user2", "2026-01-15", "07:28", "16:30", "Present", "Office HQ, Jakarta"),
            AttendanceRecord(9, "user3", "2026-01-15", "22:00", "07:00", "Present", "Office HQ, Jakarta"),
            AttendanceRecord(10, "user1", "2026-01-14", "07:35", "16:40", "Late", "Office HQ, Jakarta"),
            AttendanceRecord(11, "user2", "2026-01-14", "-", "-", "Absent", "-"),
            AttendanceRecord(12, "user3", "2026-01-14", "21:58", "07:02", "Present", "Office HQ, Jakarta"),
            AttendanceRecord(13, "user1", "2026-01-13", "07:20", "16:25", "Present", "Remote - Coworking Space")
        )
        
        // NOTE: Overtime data is now loaded from Firebase in OvertimeTab.render()
        // No need to initialize dummy data here
        
        // Time off requests will be loaded from Firebase
        timeOffRequests["team1"] = mutableListOf()
        
        
        // Leave types will be loaded from Firebase
        leaveTypes["team1"] = mutableListOf()
        
        
        // Init schedules for team1
        val t1Assignments = mutableMapOf<String, MutableMap<Int, String>>()
        
        // User 1 Schedule (January 2026)
        t1Assignments["user1"] = mutableMapOf(
            1 to "S1", 2 to "S1", 3 to "OFF", 4 to "S2", 5 to "S1", 6 to "S1", 7 to "OFF",
            8 to "S1", 9 to "S1", 10 to "OFF", 11 to "S2", 12 to "S1", 13 to "S1", 14 to "OFF",
            15 to "S1", 16 to "S1", 17 to "OFF", 18 to "S2", 19 to "S1", 20 to "S1", 21 to "OFF",
            22 to "S1", 23 to "S1", 24 to "OFF", 25 to "S2", 26 to "S1", 27 to "S1", 28 to "OFF",
            29 to "S1", 30 to "S1", 31 to "OFF"
        )
        // User 2 Schedule (January 2026)
        t1Assignments["user2"] = mutableMapOf(
            1 to "OFF", 2 to "OFF", 3 to "S1", 4 to "OFF", 5 to "OFF", 6 to "S1", 7 to "OFF",
            8 to "OFF", 9 to "OFF", 10 to "S1", 11 to "OFF", 12 to "OFF", 13 to "S1", 14 to "OFF",
            15 to "OFF", 16 to "OFF", 17 to "S1", 18 to "OFF", 19 to "OFF", 20 to "OFF", 21 to "OFF",
            22 to "OFF", 23 to "OFF", 24 to "S1", 25 to "OFF", 26 to "OFF", 27 to "S1", 28 to "OFF",
            29 to "OFF", 30 to "OFF", 31 to "S1"
        )
        // User 3 Schedule (January 2026) - Night shift
        t1Assignments["user3"] = mutableMapOf(
            1 to "S3", 2 to "S3", 3 to "S3", 4 to "S3", 5 to "S3", 6 to "OFF", 7 to "OFF",
            8 to "S3", 9 to "S3", 10 to "S3", 11 to "S3", 12 to "S3", 13 to "OFF", 14 to "OFF",
            15 to "S3", 16 to "S3", 17 to "S3", 18 to "S3", 19 to "S3", 20 to "S3", 21 to "OFF",
            22 to "S3", 23 to "S3", 24 to "S3", 25 to "S3", 26 to "S3", 27 to "OFF", 28 to "OFF",
            29 to "S3", 30 to "S3", 31 to "S3"
        )
        
        schedules["team1"] = Schedule(1, 2026, t1Assignments)
        
        // Init leave categories for team1
        leaveCategories["team1"] = mutableListOf(
            LeaveCategory("annual", "type_annual", "Cuti Tahunan (Annual Leave)", 12, true, false, false, "emerald"),
            LeaveCategory("sick", "type_health", "Sakit (Sick Leave)", 0, false, true, false, "blue"),
            LeaveCategory("maternity", "type_health", "Melahirkan (Maternity)", 90, false, false, false, "pink"),
            LeaveCategory("paternity", "type_family", "Suami Siaga (Paternity)", 2, false, false, false, "cyan"),
            LeaveCategory("marriage", "type_family", "Menikah (Marriage)", 3, false, false, false, "violet"),
            LeaveCategory("bereavement", "type_family", "Duka Cita (Bereavement)", 2, false, false, false, "slate"),
            LeaveCategory("unpaid", "type_unpaid", "Unpaid Leave", 0, false, false, true, "rose")
        )
    }
    
    // Initialize mock data for a specific team (for demo purposes)
    fun initMockDataForTeam(teamId: String) {
        console.log("🎭 Initializing mock data for team:", teamId)
        
        // NOTE: Attendance data is now loaded from Firebase in TeamHandlers.openTeam()
        // Only initialize other mock data (overtime, time off, etc.)
        
        
        
        // NOTE: Overtime data is now loaded from Firebase in OvertimeTab.render()
        // No need to initialize dummy data
        
        
        
        // Time off requests will be loaded from Firebase
        if (timeOffRequests[teamId] == null) {
            timeOffRequests[teamId] = mutableListOf()
            console.log("✅ Time off requests initialized (will load from Firebase)")
        }
        
        
        
        // Leave types and categories will be loaded from Firebase
        if (leaveTypes[teamId] == null) {
            leaveTypes[teamId] = mutableListOf()
            console.log("✅ Leave types initialized (will load from Firebase)")
        }
        
        if (leaveCategories[teamId] == null) {
            leaveCategories[teamId] = mutableListOf()
            console.log("✅ Leave categories initialized (will load from Firebase)")
        }
        
        
        
        if (holidays[teamId] == null) {
            holidays[teamId] = mutableListOf(
                features.attendance.domain.Holiday("h1", "2026-01-01", "New Year's Day", "Happy New Year!"),
                features.attendance.domain.Holiday("h2", "2026-02-14", "Valentine's Day", "Love is in the air"),
                features.attendance.domain.Holiday("h3", "2026-03-31", "Eid al-Fitr", "Islamic Holiday"),
                features.attendance.domain.Holiday("h4", "2026-05-01", "Labor Day", "International Workers' Day"),
                features.attendance.domain.Holiday("h5", "2026-08-17", "Independence Day", "Indonesia Independence Day"),
                features.attendance.domain.Holiday("h6", "2026-12-25", "Christmas Day", "Merry Christmas!")
            )
            console.log("✅ Mock holidays added")
        }
        console.log("🎉 Mock data initialization complete for team:", teamId)
    }
}
